import React, { useEffect, useState } from 'react';
import { MdAdd, MdEdit, MdDelete } from 'react-icons/md';
import { toast } from 'react-toastify';
import DeleteModel from '../Common/DeleteModel';
import { useAuth } from '../../Context/AuthContext';
import PeerReferencesModal from './PeerReferencesModal';
import { saveProviderPeerReference, deleteProviderPeerReference, getAllProviderPeerReferences } from '../../Services/ProviderService';

const PROVIDER_ID_STORAGE_KEY = 'providerForm.providerId';
const ORGANIZATION_ID_STORAGE_KEY = 'providerForm.organizationId';

const getProviderIdFromStorage = () => {
  try {
    return localStorage.getItem(PROVIDER_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read provider id from localStorage:', error);
    return null;
  }
};

const getOrganizationIdFromStorage = () => {
  try {
    return localStorage.getItem(ORGANIZATION_ID_STORAGE_KEY);
  } catch (error) {
    console.error('Failed to read organization id from localStorage:', error);
    return null;
  }
};
const getInitialPeerReferences = (initialData) => {
  return initialData?.references || initialData?.peerReferences || [];
};

export default function PeerReferences({ initialData, providerId, onSaveSuccess, onContinue }) {
  const { token } = useAuth();
  const [items, setItems] = useState(() => getInitialPeerReferences(initialData));
  const [showModal, setShowModal] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingIndex, setDeletingIndex] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, index: null, item: null, isLoading: false });

  const handleAdd = () => {
    setEditingIndex(null);
    setShowModal(true);
  };

  const handleEdit = (index) => {
    setEditingIndex(index);
    setShowModal(true);
  };

  const loadPeerReferences = async () => {
    if (!token || !providerId) return;

    setLoading(true);
    try {
      const response = await getAllProviderPeerReferences(token, providerId);
      if (response?.is_success) {
        const refreshed = Array.isArray(response.data) ? response.data : [];
        setItems(refreshed);
        if (onSaveSuccess) onSaveSuccess(refreshed);
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        // toast.error(response?.message || 'Failed to load peer references');
        console.error('Failed to load peer references:', response?.message || 'Unknown error');
      }
    } catch (error) {
      console.error('Error loading peer references:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token || !providerId) return;
    if (getInitialPeerReferences(initialData).length > 0) return;
    loadPeerReferences();
  }, [token, providerId, initialData]);

  const handleSave = async (values) => {
    const isUpdate = Boolean(values.id || values.Id || values.peer_reference_id);
    const payload = {
      ...values,
      provider_id: providerId || values.provider_id || getProviderIdFromStorage(),
      organization_id:
        values.organization_id || getOrganizationIdFromStorage() || null,
      type: isUpdate ? 'update' : 'create',
    };

    if (!token || !providerId) {
      const updated = [...items];
      if (editingIndex !== null) {
        updated[editingIndex] = payload;
      } else {
        updated.push(payload);
      }
      setItems(updated);
      setShowModal(false);
      setEditingIndex(null);
      if (onSaveSuccess) onSaveSuccess(updated);
      return;
    }

    setSaving(true);
    try {
      const response = await saveProviderPeerReference(token, payload);
      if (response?.is_success) {
        await loadPeerReferences();
        setShowModal(false);
        setEditingIndex(null);
        toast.success(editingIndex !== null ? 'Peer reference updated' : 'Peer reference added');
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
      } else {
        toast.error(response?.message || 'Failed to save peer reference');
      }
    } catch (error) {
      console.error('Error saving peer reference:', error);
      toast.error(error?.message || 'Failed to save peer reference');
    } finally {
      setSaving(false);
    }
  };

  const openDeleteModal = (index) => {
    setDeleteModal({ isOpen: true, index, item: items[index], isLoading: false });
  };

  const closeDeleteModal = () => setDeleteModal({ isOpen: false, index: null, item: null, isLoading: false });

  const confirmDelete = async () => {
    if (deleteModal.index === null || deleteModal.index === undefined) return;
    setDeleteModal((prev) => ({ ...prev, isLoading: true }));
    try {
      await handleDelete(deleteModal.index);
      closeDeleteModal();
    } catch (error) {
      closeDeleteModal();
    }
  };

  const handleDelete = async (index) => {
    const item = items[index];
    const originalItems = [...items];

    if (!token || !providerId || !item || !(item.id || item.Id || item.peer_reference_id)) {
      return;
    }

    setDeletingIndex(index);
    try {
      const id = item.id || item.Id || item.peer_reference_id;
      const response = await deleteProviderPeerReference(token, id);
      if (response?.is_success) {
        const refreshed = await loadPeerReferences();
        const nextItems = Array.isArray(refreshed) ? refreshed : items.filter((_, i) => i !== index);
        setItems(nextItems);
        if (onSaveSuccess) onSaveSuccess(nextItems);
        return;
      } else if (response?.is_success === false && response?.status_code === 500) {
        toast.error('Internal Server Error. Please Contact Support Team.');
        setItems(originalItems);
        if (onSaveSuccess) onSaveSuccess(originalItems);
        return;
      } else {
        toast.error(response?.message || 'Failed to delete peer reference');
        setItems(originalItems);
        if (onSaveSuccess) onSaveSuccess(originalItems);
      }
    } catch (error) {
      console.error('Error deleting peer reference:', error);
      setItems(originalItems);
      if (onSaveSuccess) onSaveSuccess(originalItems);
    } finally {
      setDeletingIndex(null);
    }
  };

  const handleContinueClick = () => {
    if (onContinue) onContinue();
  };

  return (
    <>
      <div className="mb-4 p-4 rounded-4 border bg-light-subtle">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h6 className="fw-bold hc-text-primary m-0">Peer References</h6>
          <button type="button" className="btn btn-sm hc-bg-primary text-white rounded-3 px-3 py-1 d-flex align-items-center gap-2" onClick={handleAdd} disabled={loading || saving}>
            <MdAdd size={18} /> Add
          </button>
        </div>



        {loading ?(
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-4 text-muted small">
              No peer references added yet. Click "Add" to create one.
            </div>
          ) : (

          <div className="row g-3 mb-3">
            {
              items.map((item, idx) => (
                <React.Fragment key={idx}>
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Reference Name
                    </div>

                    <div className="fw-semibold text-dark">
                     {item.reference_name || 'Unnamed Reference'}
                    </div>

                  </div>
                </div>
              </div>


  
              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Relationship
                    </div>

                    <div className="fw-semibold text-dark">
                     {item.relationship_type || item.relationshipType || 'N/A'}
                    </div>

                  </div>
                </div>
              </div>





              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Status
                    </div>

                    <div className="fw-semibold text-dark">
                      {item.status_name || item.reference_status === 1 ? 'Active' : item.reference_status === 0 ? 'Inactive' : 'N/A'}
                    </div>

                  </div>
                </div>
              </div>



              <div className="col-12 col-md-6 col-lg-3">
                <div className="card h-100 border-0 shadow-lg rounded-4">
                  <div className="card-body">

                    <div className="text-muted small mb-2">
                      Actions
                    </div>

                    <div className="d-flex gap-2">

                      <button type="button" className="btn btn-sm btn-outline-primary rounded-3" onClick={() => handleEdit(idx)} disabled={saving || deletingIndex === idx}><MdEdit size={16} /></button>
                      <button type="button" className="btn btn-sm btn-danger rounded-3" onClick={() => openDeleteModal(idx)} disabled={saving || deletingIndex === idx}><MdDelete size={16} /></button>

                    </div>

                  </div>
                </div>
              </div> 
                </React.Fragment>

              ))}
              

            </div>

            )}



        <PeerReferencesModal
          isOpen={showModal}
          onClose={() => { setShowModal(false); setEditingIndex(null); }}
          onSave={handleSave}
          initialData={editingIndex !== null ? items[editingIndex] : null}
          isLoading={saving}
        />
      </div>

      <DeleteModel
        isOpen={deleteModal.isOpen}
        title="Confirm Deletion"
        entityLabel="peer reference"
        itemName={deleteModal.item ? (deleteModal.item.reference_name || 'this peer reference') : 'this peer reference'}
        description="This will permanently delete this peer reference. This action cannot be undone."
        onConfirm={confirmDelete}
        onClose={closeDeleteModal}
        isLoading={deleteModal.isLoading}
      />

      <div className="d-flex justify-content-end mt-4">
        <button
          type="button"
          className="btn btn-sm hc-bg-primary text-white rounded-3 px-4 py-2"
          onClick={handleContinueClick}
        >
          Continue
        </button>
      </div>
    </>
  );
}
